import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, canWrite } from "@/lib/auth";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Radio, Plus, ShieldAlert, RefreshCw, Search } from "lucide-react";

// ---------------------------------------------------------------------------
// MP12 Module 4 — Sync Command Desk list page.
// Internal-only; RBAC gated. Sensitive fields are redacted server-side for
// non-authorised viewers, so we can safely display whatever comes back.
// ---------------------------------------------------------------------------

const SYNC_STATUS_LABELS = {
  DRAFT: "DRAFT",
  IN_REVIEW: "IN REVIEW",
  MATCHING: "MATCHING",
  REVIEW_FILE_IN_PREPARATION: "REVIEW FILE IN PREPARATION",
  READY_FOR_APPROVAL: "READY FOR APPROVAL",
  APPROVED_SENT: "APPROVED / SENT",
  CLOSED_EXPIRED: "CLOSED / EXPIRED",
  DECLINED: "DECLINED",
};

const STATUS_STYLE = {
  DRAFT:                      { bg: "hsl(220 8% 94%)",  fg: "hsl(220 6% 40%)",  border: "hsl(220 6% 78%)" },
  IN_REVIEW:                  { bg: "hsl(40 55% 94%)",  fg: "hsl(35 60% 28%)",  border: "hsl(40 55% 76%)" },
  MATCHING:                   { bg: "hsl(210 45% 94%)", fg: "hsl(210 55% 30%)", border: "hsl(210 45% 74%)" },
  REVIEW_FILE_IN_PREPARATION: { bg: "hsl(210 45% 94%)", fg: "hsl(210 55% 30%)", border: "hsl(210 45% 74%)" },
  READY_FOR_APPROVAL:         { bg: "hsl(40 55% 94%)",  fg: "hsl(35 60% 28%)",  border: "hsl(40 55% 76%)" },
  APPROVED_SENT:              { bg: "hsl(150 45% 92%)", fg: "hsl(150 45% 24%)", border: "hsl(150 40% 66%)" },
  CLOSED_EXPIRED:             { bg: "hsl(220 8% 94%)",  fg: "hsl(220 6% 40%)",  border: "hsl(220 6% 78%)" },
  DECLINED:                   { bg: "hsl(0 55% 94%)",   fg: "hsl(0 65% 30%)",   border: "hsl(0 55% 72%)" },
};

export function SyncStatusBadge({ value, testid }) {
  const st = STATUS_STYLE[value] || STATUS_STYLE.DRAFT;
  return (
    <Badge
      variant="outline"
      className="text-[10px] uppercase tracking-[0.14em] font-semibold"
      style={{ background: st.bg, color: st.fg, borderColor: st.border }}
      data-testid={testid || "sync-status-badge"}
    >
      {SYNC_STATUS_LABELS[value] || value || "—"}
    </Badge>
  );
}

export default function SyncCommandDesk() {
  const { user } = useAuth();
  const writer = canWrite(user);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("__all__");
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    media_project_context: "",
    opportunity_source: "",
    media_type: "",
    deadline: "",
    cisn_reference: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (q.trim())            params.q = q.trim();
      if (status && status !== "__all__") params.status = status;
      const r = await api.get("/sync-briefs", { params });
      setRows(r.data?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load sync briefs.");
    } finally {
      setLoading(false);
    }
  }, [q, status]);

  useEffect(() => { load(); }, [load]);

  async function createBrief() {
    if (!form.media_project_context.trim() && !form.opportunity_source.trim()) {
      toast.error("Provide a media/project context or opportunity source before creating.");
      return;
    }
    setCreating(true);
    try {
      const r = await api.post("/sync-briefs", form);
      toast.success(`Sync brief ${r.data.internal_brief_id} created.`);
      setCreateOpen(false);
      setForm({ media_project_context: "", opportunity_source: "", media_type: "", deadline: "", cisn_reference: "" });
      navigate(`/advanced/sync-command-desk/${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create sync brief.");
    } finally {
      setCreating(false);
    }
  }

  if (!writer) {
    return <div className="p-6 text-[13px] text-muted-foreground" data-testid="scd-readonly">Sync Command Desk is limited to authorised internal users.</div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4" data-testid="sync-command-desk-page">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Radio className="w-5 h-5" style={{ color: "hsl(347 60% 22%)" }} />
            <h1 className="font-display text-[22px]">Sync Command Desk</h1>
            <Badge variant="outline" className="text-[10px] uppercase tracking-[0.14em] font-semibold">Internal-only</Badge>
          </div>
          <p className="text-[12.5px] text-muted-foreground">
            Sync briefs, controlled catalogue matching, and versioned submission history. No external AI or third-party matching services.
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={load} data-testid="scd-refresh"><RefreshCw className="w-3.5 h-3.5 mr-1.5" />Refresh</Button>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button size="sm" style={{ background: "hsl(347 72% 22%)" }} data-testid="scd-new"><Plus className="w-3.5 h-3.5 mr-1.5" />New sync brief</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="scd-new-dialog">
              <DialogHeader>
                <DialogTitle>New sync brief</DialogTitle>
                <DialogDescription className="text-[12.5px]">
                  Only fields needed to open the brief. All other details are captured on the editor page.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-[10.5px] uppercase tracking-wide">Media / project context</Label>
                  <Textarea value={form.media_project_context} onChange={(e) => setForm({ ...form, media_project_context: e.target.value })} rows={2} className="mt-1" data-testid="scd-new-context" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10.5px] uppercase tracking-wide">Opportunity source</Label>
                    <Input value={form.opportunity_source} onChange={(e) => setForm({ ...form, opportunity_source: e.target.value })} placeholder="agency, supervisor, ..." className="mt-1 h-8 text-[12px]" data-testid="scd-new-source" />
                  </div>
                  <div>
                    <Label className="text-[10.5px] uppercase tracking-wide">Media type</Label>
                    <Input value={form.media_type} onChange={(e) => setForm({ ...form, media_type: e.target.value })} placeholder="trailer, film, ad, game" className="mt-1 h-8 text-[12px]" data-testid="scd-new-media" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10.5px] uppercase tracking-wide">Deadline</Label>
                    <Input value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} placeholder="YYYY-MM-DD or freeform" className="mt-1 h-8 text-[12px]" data-testid="scd-new-deadline" />
                  </div>
                  <div>
                    <Label className="text-[10.5px] uppercase tracking-wide">CISN / CIS-IDN (restricted)</Label>
                    <Input
                      value={form.cisn_reference}
                      onChange={(e) => setForm({ ...form, cisn_reference: e.target.value })}
                      placeholder="visible to Super Admin / Catalog Manager only"
                      className="mt-1 h-8 text-[12px]"
                      data-testid="scd-new-cisn"
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={creating} data-testid="scd-new-cancel">Cancel</Button>
                <Button onClick={createBrief} disabled={creating} data-testid="scd-new-save" style={{ background: "hsl(347 72% 22%)" }}>{creating ? "Creating…" : "Create draft"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardContent className="p-3 flex items-center gap-2 flex-wrap">
          <div className="flex-1 min-w-[220px] relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search context, opportunity, media type, brief id…" className="pl-7 h-8 text-[12px]" data-testid="scd-search" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-8 w-[220px] text-[12px]" data-testid="scd-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All statuses</SelectItem>
              {Object.entries(SYNC_STATUS_LABELS).map(([code, label]) => (
                <SelectItem key={code} value={code} data-testid={`scd-status-opt-${code}`}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-[15px]">Briefs ({rows.length})</CardTitle>
          <CardDescription className="text-[11.5px]">Sensitive identity fields are redacted for viewers outside Super Admin / Catalog Manager.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-[12.5px] text-muted-foreground">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-[12.5px] text-muted-foreground italic" data-testid="scd-empty">No sync briefs match your filters.</div>
          ) : (
            <ul className="divide-y border rounded-md" data-testid="scd-list">
              {rows.map((b) => (
                <li key={b.id} className="p-3 flex items-center gap-3" data-testid={`scd-row-${b.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[11px] text-muted-foreground">{b.internal_brief_id}</span>
                      <span className="text-[13px] font-medium truncate">{b.media_project_context || "(no context)"}</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap gap-x-3">
                      {b.opportunity_source ? <span>Source: {b.opportunity_source}</span> : null}
                      {b.media_type ? <span>Media: {b.media_type}</span> : null}
                      {b.deadline ? <span>Deadline: {b.deadline}</span> : null}
                      {b.assigned_internal_reviewer ? <span>Reviewer: {b.assigned_internal_reviewer}</span> : null}
                    </div>
                  </div>
                  <SyncStatusBadge value={b.status} testid={`scd-row-status-${b.id}`} />
                  <Button asChild size="sm" variant="ghost" data-testid={`scd-open-${b.id}`}>
                    <Link to={`/advanced/sync-command-desk/${b.id}`}>Open</Link>
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
